import clsx from "clsx";
import { useTranslation } from "react-i18next";
import type { TimelineEvent } from "../../../utils/timeline";
import { TimelineIcon } from "../icons";

interface TimelineDayProps {
  events: TimelineEvent[];
  /** Optional heading shown above the day (only when there's >1 day). */
  header?: string | null;
  /** Id of the event happening now, or null. */
  currentId: string | null;
  /** Id of the next upcoming event, or null. */
  nextId: string | null;
}

/**
 * One day of the timeline: an optional date/label header followed by its
 * events as a vertical schedule. The event happening now (and the next one)
 * are flagged by id from {@link useCurrentEvent}.
 */
export const TimelineDay = ({ events, header, currentId, nextId }: TimelineDayProps) => {
  const { t } = useTranslation();

  return (
    <div className="timeline-day">
      {header && <h2 className="timeline-day-header">{header}</h2>}
      <ol className="timeline">
        {events.map((event) => {
          const isNow = event.id === currentId;
          const isNext = event.id === nextId;
          return (
            <li
              key={event.id}
              className={clsx("timeline-item", { "is-now": isNow, "is-next": isNext })}
            >
              <div className="timeline-marker">
                <span className="timeline-icon">
                  <TimelineIcon iconKey={event.icon} />
                </span>
              </div>
              <div className="timeline-content">
                <div className="timeline-head">
                  <span className="timeline-time">{event.time}</span>
                  {isNow && (
                    <span className="timeline-badge is-now">{t("timeline.now")}</span>
                  )}
                  {isNext && (
                    <span className="timeline-badge is-next">{t("timeline.upNext")}</span>
                  )}
                </div>
                <p className="timeline-name">{event.name}</p>
                {event.description && (
                  <p className="timeline-description">{event.description}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
